import { useState } from 'react'
import {
  useBetaRequests,
  useBetaPendingCount,
  useApproveBetaRequest,
  useRejectBetaRequest,
} from '@/hooks/useBetaRequests'
import { usePlans } from '@/hooks/usePlans'
import { DataTable, type ColumnDef } from '@/components/common/DataTable'
import { SearchInput } from '@/components/common/SearchInput'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { BetaRequest, BetaRequestsFilter, ApproveBetaRequestPayload } from '@/types'
import { formatDate } from '@/lib/utils'
import { Check, X, Eye } from 'lucide-react'

const REFERRAL_LABELS: Record<string, string> = {
  social_media: 'Redes sociales',
  referral: 'Referido',
  search: 'Busqueda',
  event: 'Evento',
  other: 'Otro',
}

export const BetaRequestsPage = () => {
  const [activeTab, setActiveTab] = useState('pending')
  const [filters, setFilters] = useState<BetaRequestsFilter>({ page: 1, limit: 20 })
  const [search, setSearch] = useState('')

  // Dialogs
  const [selectedRequest, setSelectedRequest] = useState<BetaRequest | null>(null)
  const [showDetail, setShowDetail] = useState(false)
  const [showApprove, setShowApprove] = useState(false)
  const [showReject, setShowReject] = useState(false)

  // Approve form
  const [approveForm, setApproveForm] = useState<ApproveBetaRequestPayload>({
    password: '',
    subscription_level: '',
    initial_credits: undefined,
  })

  // Reject form
  const [rejectReason, setRejectReason] = useState('')

  const currentFilters: BetaRequestsFilter = {
    ...filters,
    search: search || undefined,
    status: activeTab !== 'all' ? activeTab : undefined,
  }

  const { data, isLoading } = useBetaRequests(currentFilters)
  const { data: pendingCount } = useBetaPendingCount()
  const { data: plans } = usePlans()
  const approveMutation = useApproveBetaRequest()
  const rejectMutation = useRejectBetaRequest()

  const handleApprove = () => {
    if (!selectedRequest || !approveForm.password || !approveForm.subscription_level) return
    approveMutation.mutate(
      { id: selectedRequest.id, payload: approveForm },
      {
        onSuccess: () => {
          setShowApprove(false)
          setSelectedRequest(null)
          setApproveForm({ password: '', subscription_level: '', initial_credits: undefined })
        },
      }
    )
  }

  const handleReject = () => {
    if (!selectedRequest) return
    rejectMutation.mutate(
      { id: selectedRequest.id, payload: { reason: rejectReason || undefined } },
      {
        onSuccess: () => {
          setShowReject(false)
          setSelectedRequest(null)
          setRejectReason('')
        },
      }
    )
  }

  const openApprove = (req: BetaRequest) => {
    setSelectedRequest(req)
    setApproveForm({
      password: '',
      subscription_level: req.desired_plan_id || '',
      initial_credits: undefined,
    })
    setShowApprove(true)
  }

  const openReject = (req: BetaRequest) => {
    setSelectedRequest(req)
    setRejectReason('')
    setShowReject(true)
  }

  const openDetail = (req: BetaRequest) => {
    setSelectedRequest(req)
    setShowDetail(true)
  }

  const columns: ColumnDef<BetaRequest>[] = [
    {
      key: 'name',
      header: 'Nombre',
      cell: (row: BetaRequest) => `${row.name} ${row.lastname}`,
    },
    {
      key: 'email',
      header: 'Email',
      cell: (row: BetaRequest) => row.email,
    },
    {
      key: 'company_name',
      header: 'Empresa',
      cell: (row: BetaRequest) => row.company_name || '-',
    },
    {
      key: 'country',
      header: 'Pais',
      cell: (row: BetaRequest) => row.country,
    },
    {
      key: 'desired_plan_id',
      header: 'Plan deseado',
      cell: (row: BetaRequest) => row.desired_plan_id || '-',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row: BetaRequest) => <StatusBadge status={row.status} />,
    },
    {
      key: 'created_at',
      header: 'Fecha',
      cell: (row: BetaRequest) => formatDate(row.created_at),
    },
    {
      key: 'actions',
      header: 'Acciones',
      cell: (row: BetaRequest) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => openDetail(row)}>
            <Eye className="h-4 w-4" />
          </Button>
          {row.status === 'pending' && (
            <>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openApprove(row)}
                className="text-green-600 hover:text-green-700"
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => openReject(row)}
                className="text-red-600 hover:text-red-700"
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
        </div>
      ),
    },
  ]

  const pendingBadge = pendingCount?.count ? pendingCount.count : 0

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Solicitudes Beta</h1>
          <p className="text-muted-foreground">
            Gestiona las solicitudes de acceso a la plataforma
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="pending" className="gap-2">
              Pendientes
              {pendingBadge > 0 && (
                <Badge variant="error" className="ml-1 h-5 min-w-5 px-1 text-xs">
                  {pendingBadge}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="approved">Aprobados</TabsTrigger>
            <TabsTrigger value="rejected">Rechazados</TabsTrigger>
            <TabsTrigger value="all">Todos</TabsTrigger>
          </TabsList>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nombre, email, empresa..."
          />
        </div>

        <TabsContent value={activeTab} className="mt-4">
          <DataTable
            columns={columns}
            data={data?.requests || []}
            isLoading={isLoading}
            keyExtractor={(r) => r.id}
            pagination={{
              page: data?.page || 1,
              pages: data?.pages || 1,
              onPageChange: (page) => setFilters((f) => ({ ...f, page })),
            }}
          />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalle de Solicitud</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground">Nombre</p>
                  <p className="font-medium">
                    {selectedRequest.name} {selectedRequest.lastname}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p className="font-medium">{selectedRequest.email}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Telefono</p>
                  <p className="font-medium">{selectedRequest.phone}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pais</p>
                  <p className="font-medium">{selectedRequest.country}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Empresa</p>
                  <p className="font-medium">{selectedRequest.company_name || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Cargo</p>
                  <p className="font-medium">{selectedRequest.job_title || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Tamano empresa</p>
                  <p className="font-medium">{selectedRequest.company_size || '-'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Plan deseado</p>
                  <p className="font-medium">{selectedRequest.desired_plan_id}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Referencia</p>
                  <p className="font-medium">
                    {selectedRequest.referral_source
                      ? REFERRAL_LABELS[selectedRequest.referral_source] ||
                        selectedRequest.referral_source
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Status</p>
                  <StatusBadge status={selectedRequest.status} />
                </div>
              </div>
              {selectedRequest.use_case && (
                <div>
                  <p className="text-muted-foreground">Caso de uso</p>
                  <p className="font-medium">{selectedRequest.use_case}</p>
                </div>
              )}
              {selectedRequest.rejection_reason && (
                <div>
                  <p className="text-muted-foreground">Razon de rechazo</p>
                  <p className="font-medium">{selectedRequest.rejection_reason}</p>
                </div>
              )}
              {selectedRequest.reviewed_by && (
                <div>
                  <p className="text-muted-foreground">Revisado por</p>
                  <p className="font-medium">
                    {selectedRequest.reviewed_by} (
                    {selectedRequest.reviewed_at ? formatDate(selectedRequest.reviewed_at) : ''})
                  </p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Fecha solicitud</p>
                <p className="font-medium">{formatDate(selectedRequest.created_at)}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedRequest?.status === 'pending' && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDetail(false)
                    if (selectedRequest) openReject(selectedRequest)
                  }}
                  className="text-red-600"
                >
                  Rechazar
                </Button>
                <Button
                  onClick={() => {
                    setShowDetail(false)
                    if (selectedRequest) openApprove(selectedRequest)
                  }}
                >
                  Aprobar
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={showApprove} onOpenChange={setShowApprove}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aprobar solicitud</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Se creara una cuenta para{' '}
                <strong>
                  {selectedRequest.name} {selectedRequest.lastname}
                </strong>{' '}
                ({selectedRequest.email})
              </p>

              <div className="space-y-2">
                <Label>Contrasena temporal *</Label>
                <Input
                  type="text"
                  value={approveForm.password}
                  onChange={(e) => setApproveForm((f) => ({ ...f, password: e.target.value }))}
                  placeholder="Minimo 8 caracteres"
                />
              </div>

              <div className="space-y-2">
                <Label>Plan *</Label>
                <Select
                  value={approveForm.subscription_level}
                  onValueChange={(v) => setApproveForm((f) => ({ ...f, subscription_level: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecciona un plan" />
                  </SelectTrigger>
                  <SelectContent>
                    {(plans || []).map((plan) => (
                      <SelectItem key={plan.plan_id || plan.name} value={plan.name}>
                        {plan.display_name || plan.name} (${plan.price_monthly_usd}/mes)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Creditos iniciales (opcional)</Label>
                <Input
                  type="number"
                  value={approveForm.initial_credits || ''}
                  onChange={(e) =>
                    setApproveForm((f) => ({
                      ...f,
                      initial_credits: e.target.value ? parseInt(e.target.value) : undefined,
                    }))
                  }
                  placeholder="0"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApprove(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleApprove}
              disabled={
                approveMutation.isPending ||
                !approveForm.password ||
                approveForm.password.length < 8 ||
                !approveForm.subscription_level
              }
            >
              {approveMutation.isPending ? 'Aprobando...' : 'Aprobar y crear usuario'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showReject} onOpenChange={setShowReject}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Rechazar solicitud</DialogTitle>
          </DialogHeader>
          {selectedRequest && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Se rechazara la solicitud de{' '}
                <strong>
                  {selectedRequest.name} {selectedRequest.lastname}
                </strong>{' '}
                ({selectedRequest.email})
              </p>

              <div className="space-y-2">
                <Label>Razon del rechazo (opcional)</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explica brevemente el motivo..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReject(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleReject}
              disabled={rejectMutation.isPending}
            >
              {rejectMutation.isPending ? 'Rechazando...' : 'Rechazar solicitud'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
